//@ts-check
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {useDataTableV2} from '@/hooks/useDataTableV2'
import {PromotionListColConfig} from '@/services/MockData/Promotions/PromotionListColConfig'
import {downloadPromotionList, downloadPromotionTemplate, getPromotionList} from '@/services/promotionListService'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import gb from '@/utils/growthbookUtils'
import {keepPreviousData, useQuery} from '@tanstack/react-query'
import {useCallback, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'

/** @typedef {{column: string, direction: 'asc' | 'desc'}} SortEntry */

// Kept in sync with the buildQueryParams fallback below so the header's sort arrow matches the actual request
const DEFAULT_SORT = /** @type {SortEntry} */ ({column: 'activeStartDate', direction: 'desc'})

/** @param {SortEntry | null} sorting @param {number} pageIndex @param {number} pageSize */
const buildQueryParams = (sorting, pageIndex, pageSize) => {
  const params = new URLSearchParams()
  params.append('sortBy', sorting?.column ?? DEFAULT_SORT.column)
  params.append('sortOrder', (sorting?.direction ?? DEFAULT_SORT.direction).toUpperCase())
  params.append('page', String(pageIndex))
  params.append('pageSize', String(pageSize))
  return params.toString()
}

/**
 * Data source hook for the v2 Promotion List table — wires `useDataTableV2` state
 * (pagination/sorting/filters) into the `getPromotionList` query, scoped to non-combo products.
 * @param {number} [defaultPageSize]
 */
export const usePromotionListTableV2 = (defaultPageSize = DEFAULT_TABLE_PAGE_SIZE) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const navigate = useNavigate()
  const formInitialData = {
    promotionTemplateName: '',
    storeSelector: '',
    actions: '',
    numberOfDays: '',
    numberOfPoints: '',
    schedule: '',
    startDate: '',
    endDate: '',
    mediaFile: [],
  }
  const [formData, setFormData] = useState({...formInitialData})
  const debounceMS = gb.getFeatureValue(growthBookFeatureFlags.ProductSearchDebounceDelay, 2000)

  const {
    pageIndex,
    pageSize,
    sorting,
    activeFilters,
    selectedRows,
    isAllRowsSelected,
    resetKey,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    handleResetAll,
    onTableReady,
    rowSelectionStateUpdater,
  } = useDataTableV2({defaultPageSize, debounceMs: debounceMS, defaultSorting: DEFAULT_SORT})

  const searchFilters = useMemo(() => activeFilters, [activeFilters])
  const filtersKey = useMemo(() => JSON.stringify(searchFilters), [searchFilters])
  const queryParams = buildQueryParams(sorting, pageIndex, pageSize)

  const {data, isFetching} = useQuery({
    queryKey: ['PROMOTION_LIST_V2', queryParams, filtersKey, searchFilters],
    queryFn: () => getPromotionList(searchFilters, queryParams),
    placeholderData: keepPreviousData,
  })

  const handleResetAllClick = useCallback(() => {
    handleResetAll()
  }, [handleResetAll])

  const resetFilterButtonOptions = useMemo(
    () => ({onFiltersReset: handleResetAllClick, text: 'Reset All'}),
    [handleResetAllClick]
  )

  const canDownload = selectedRows.length > 0 || isAllRowsSelected

  const onDownloadClick = useCallback(async () => {
    if (isDownloading || !canDownload) return
    setIsDownloading(true)
    try {
      const ids = isAllRowsSelected ? [] : selectedRows.map(row => row.id)
      const csvData = await downloadPromotionList({ids, filters: searchFilters}, queryParams)
      downloadCSVFiles(csvData, `PromotionList_${getDateWithTimeString()}`)
    } finally {
      setIsDownloading(false)
    }
  }, [isDownloading, canDownload, isAllRowsSelected, selectedRows, searchFilters, queryParams])

  const handleCopyClick = useCallback(
    (promotionId, promotionType) => {
      navigate(`/products/create-promotion/${promotionId}`, {
        state: {
          promotionId,
          isDuplicating: true,
          promotionType,
        },
      })
    },
    [navigate]
  )

  // Function to download the promotion template file
  const downloadPromoTemplate = async () => {
    const response = await downloadPromotionTemplate()

    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = 'PromotionTemplate.xlsx'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(url)
  }

  return {
    columns: PromotionListColConfig,
    tableData: data?.data ?? [],
    isFetching,
    pageIndex: isFetching ? pageIndex : (data?.currentPage ?? pageIndex),
    pageSize: data?.pageSize ?? pageSize,
    totalRowsCount: data?.totalRecords ?? 0,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    selectedRows,
    isAllRowsSelected,
    resetKey,
    canDownload,
    isDownloading,
    onDownloadClick,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    resetFilterButtonOptions,
    onTableReady,
    rowSelectionStateUpdater,
    resetAll: handleResetAllClick,
    handleCopyClick,
    formData,
    setFormData,
    downloadPromoTemplate,
  }
}
